import React from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Settings } from 'lucide-react';
import { EnergyData, ChartSeries } from '../../types';

interface EnergyChartProps {
  data: EnergyData[];
  title: string;
  subtitle?: string;
  series: ChartSeries[];
  height?: number;
  onConfigure?: () => void;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-2xl border border-gray-100 bg-white/95 p-3.5 shadow-xl backdrop-blur-sm">
        <p className="mb-1.5 text-xs font-bold text-gray-900">{label}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2 text-xs">
            <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-gray-600">{entry.name}:</span>
            <span className="font-bold text-gray-900">{entry.value} MW</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const EnergyChart: React.FC<EnergyChartProps> = ({
  data,
  title,
  subtitle,
  series,
  height = 280,
  onConfigure,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="h-full"
    >
      <div className="flex h-full flex-col justify-between rounded-3xl border border-gray-100/80 bg-white p-6 shadow-xl shadow-gray-900/5 transition-all duration-300 hover:shadow-2xl hover:shadow-gray-900/10">
        {/* Header Block */}
        <div className="mb-4 space-y-3">
          {/* Top Row: Title + Configure Button */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-base font-bold text-gray-900 sm:text-lg" title={title}>
                {title}
              </h3>
              {subtitle && (
                <p className="mt-0.5 text-xs text-gray-500 font-medium">
                  {subtitle}
                </p>
              )}
            </div>

            {onConfigure && (
              <button
                onClick={onConfigure}
                className="flex-shrink-0 rounded-xl border border-gray-200 bg-gray-50/80 p-2 text-gray-500 shadow-sm transition-all duration-200 hover:bg-white hover:text-blue-600 hover:shadow-md"
                title="Ρύθμιση Γραφήματος"
              >
                <Settings className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Series Legends Row */}
          <div className="flex flex-wrap items-center gap-3 border-t border-gray-100/80 pt-2.5">
            {series.map((s, index) => (
              <div key={index} className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: s.color }}
                />
                <span>{s.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Chart Area */}
        <div style={{ height }} className="w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                {series.map((s, index) => (
                  <linearGradient key={index} id={`color_${s.key}_${index}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={s.color} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={s.color} stopOpacity={0.02} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#64748b', fontSize: 11 }}
                dy={6}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#64748b', fontSize: 11 }}
              />
              <Tooltip content={<CustomTooltip />} />
              {series.map((s, index) => (
                <Area
                  key={index}
                  type="monotone"
                  dataKey={s.key}
                  stroke={s.color}
                  fill={`url(#color_${s.key}_${index})`}
                  strokeWidth={2.5}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </motion.div>
  );
};

export default EnergyChart;
